import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getRecipes } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Overline, PrimaryButton } from "@/components/site/Primitives";
import { Reveal } from "@/lib/reveal";
import { Lock } from "lucide-react";

export default function DailyRecipe() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const subscribed = user && user.is_subscribed;

  useEffect(() => { getRecipes().then(setRecipes).catch(() => {}); }, [user]);

  const [today, ...rest] = recipes;

  return (
    <div data-testid="daily-recipe-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-10 text-center">
        <Overline>A fresh recipe every day · Members' kitchen</Overline>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light mt-5 mb-6 leading-none">The Daily Recipe</h1>
        <p className="font-body text-brand-body text-lg leading-relaxed max-w-2xl mx-auto">
          Each morning Agnes sets down a new recipe from her kitchen — seasonal, comforting and honest.
          {!subscribed && " The first taste is on the house; the rest is for members of the Cottage Circle."}
        </p>
      </section>

      {!subscribed && (
        <section className="max-w-3xl mx-auto px-6 pb-6">
          <div className="bg-brand-sage/20 border border-brand-linen p-8 text-center" data-testid="paywall-banner">
            <p className="font-body text-brand-body text-lg mb-5">Become a member for $9/month to unlock a new recipe every day and every Journal letter in full.</p>
            <PrimaryButton to="/membership" testid="paywall-join-btn">Become a member</PrimaryButton>
          </div>
        </section>
      )}

      {today && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-12">
          <Reveal>
            <Link to={`/daily-recipe/${today.slug}`} data-testid={`recipe-feature-${today.slug}`} className="group grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative overflow-hidden">
                <img src={today.cover} alt={today.title} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700" />
                {today.locked && <div className="absolute top-4 right-4 bg-brand-charcoal/70 text-brand-cream p-2 rounded-full"><Lock size={16} /></div>}
              </div>
              <div>
                <span className="overline text-[0.62rem]">Today's recipe{today.is_free ? " · free taste" : ""}</span>
                <h2 className="text-4xl sm:text-5xl font-light mt-3 mb-5 leading-tight group-hover:text-brand-olive transition-colors">{today.title}</h2>
                <p className="font-body text-brand-body text-lg leading-relaxed">{today.excerpt}</p>
                <span className="link-underline inline-block mt-6 font-sans text-xs uppercase tracking-[0.16em]">{today.locked ? "Preview the recipe" : "Read the recipe"}</span>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {rest.map((r, i) => (
            <Reveal key={r.slug} delay={(i % 3) * 0.08}>
              <Link to={`/daily-recipe/${r.slug}`} data-testid={`recipe-card-${r.slug}`} className="group block">
                <div className="relative overflow-hidden">
                  <img src={r.cover} alt={r.title} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700" />
                  {r.locked && <div className="absolute inset-0 bg-brand-charcoal/25 flex items-center justify-center"><Lock size={22} className="text-brand-cream" /></div>}
                </div>
                <span className="overline text-[0.62rem] mt-4 block">{r.is_free ? "Free taste" : "Members"} · {r.read_time}</span>
                <h3 className="font-serif text-2xl mt-1 group-hover:text-brand-olive transition-colors leading-snug">{r.title}</h3>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
